/* eslint-disable react-hooks/exhaustive-deps*/
import React, { useState, useEffect } from 'react';
import css from './Gallery.module.css';
import DashboardAddButton from '../../../elements/dashboard-add-button/DashboardAddButton';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowRight, faPlus, faPlusSquare } from '@fortawesome/free-solid-svg-icons';

import { withRouter } from 'react-router-dom';
import { notifyError, notifySuccess } from '../../../../components/HotToast/HotToastProvider';
import ImageCropModal from '../../../../forms/ProfileSettingsForm/ImageCropModal';

// ──────────────────────────────────────────────────────────────
// Publish-time readiness check (Subscription Gating Spec §1, §5.2)
//
// Returns a decision object describing whether the coach is allowed to
// publish this listing right now, and if not, why. Two reasons can
// block publish:
//
//   • 'no_subscription' — tier 0 / no active subscription. Per spec §1,
//     clicking Publish without a sub should open the pricing page (not
//     a modal). After successful checkout, the listing auto-publishes
//     (auto-publish wiring is a v2; today the coach just returns and
//     clicks Publish again).
//
//   • 'incomplete_profile' — missing one of the three required fields
//     (photo, bio, expertise tag). Per spec §5.2 the FE should call out
//     specifically which fields are missing so the coach can fix them.
//
// Expertise-tag check is intentionally lenient for v1: Viveka's profile
// schema doesn't yet have a standardized expertise/tag field, so we
// skip that part of the §5.2 contract and only enforce photo + bio.
// Once an expertise field is added to the profile schema, extend the
// missingFields list below to include it.
function getPublishReadiness(currentUser) {
  if (!currentUser || !currentUser.attributes) {
    return { ready: false, reason: 'unknown', missingFields: [] };
  }
  const profile = currentUser.attributes.profile || {};
  const metadata = profile.metadata || {};
  const subscriptionDetails = metadata.subscriptionDetails || null;
  const tier =
    subscriptionDetails && typeof subscriptionDetails.subscription === 'number'
      ? subscriptionDetails.subscription
      : 0;
  if (tier < 1) {
    return { ready: false, reason: 'no_subscription', missingFields: [] };
  }

  // Profile completeness — photo + bio. Expertise check deferred.
  const missingFields = [];
  if (!profile.bio || !profile.bio.toString().trim().length) {
    missingFields.push('a coaching bio');
  }
  const hasProfileImage = !!(
    profile.profileImage ||
    profile.profileImageId ||
    (profile.image && profile.image.id) ||
    (currentUser.profileImage && currentUser.profileImage.id)
  );
  if (!hasProfileImage) {
    missingFields.push('a profile photo');
  }
  if (missingFields.length > 0) {
    return { ready: false, reason: 'incomplete_profile', missingFields };
  }

  return { ready: true, reason: null, missingFields: [] };
}

function Gallery(props) {
  const {
    onPublishListingDraft,
    onImageUpload,
    onUpdateListing,
    images,
    listing,
    currentUser,
  } = props;
  const [videos, setVideos] = useState(['']);
  const [imageFields, setImageFields] = useState([{ id: '', src: '' }]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropFileName, setCropFileName] = useState('');

  const inputToState = (index, event) => {
    event.persist();
    let tempArray = videos.slice();
    tempArray[index] = event.target.value;
    setVideos(tempArray);
  };

  const addInput = () => {
    if (
      /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/gm.test(
        videos[videos.length - 1]
      )
    ) {
      let tempArray = videos.slice();
      tempArray[tempArray.length] = '';
      setVideos(tempArray);
    } else {
      notifyError('Add a valid video url to add another one');
    }
  };

  const removeInput = key => {
    if (videos.length > 1) {
      let tempArray = videos.slice();
      setVideos(tempArray.filter((x, index) => index !== key));
    } else {
      setVideos(['']);
    }
  };

  const removePic = key => {
    let tempArray = imageFields.filter(x => x.id.uuid !== key);
    if (tempArray.length > 0) {
      setImageFields(tempArray);
    } else {
      setImageFields([imageFields[0]]);
    }
  };

  const fileUpload = event => {
    const file = event.target.files[0];
    if (file) {
      // Open crop modal instead of uploading directly
      const objectUrl = URL.createObjectURL(file);
      setCropImageSrc(objectUrl);
      setCropFileName(file.name);
      setCropModalOpen(true);
      // Reset the input so re-selecting the same file triggers onChange
      event.target.value = '';
    }
  };

  const handleCropCancel = () => {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropModalOpen(false);
    setCropImageSrc(null);
    setCropFileName('');
  };

  const handleCropConfirm = croppedFile => {
    setCropModalOpen(false);
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);

    setUploading(true);
    onImageUpload({
      id: `${croppedFile.name}_${Date.now()}`,
      file: croppedFile,
    }).then(res => {
      let reader = new FileReader();
      reader.readAsDataURL(croppedFile);
      reader.addEventListener('load', thumb => {
        let tempArray = imageFields.slice();
        tempArray[tempArray.length] = {
          id: res.data.data.id,
          src: thumb.target.result,
        };
        setImageFields(tempArray);
        setUploading(false);
      });
    });
  };

  const getImageFromState = () => {
    let tempArray = [];
    for (let i = 0; i < imageFields.length; i++) {
      tempArray[i] = imageFields[i].id;
    }
    return tempArray;
  };

  const validateFields = () => {
    let validationErrors = [];
    let isValid = true;

    let videosAreValid = true;
    let errorIn = [];

    for (let i = 0; i < videos.length; i++) {
      const isNotValid =
        !/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/gm.test(
          videos[i]
        ) && i !== 0;
      if (isNotValid) {
        if (videosAreValid) {
          videosAreValid = false;
        }
        errorIn[errorIn.length] = i + 1;
      }
    }

    if (!videosAreValid) {
      validationErrors.push(`Invalid video url(s), Check video fields ${errorIn.join(', ')}`);
      setErrors(validationErrors);
      isValid = false;
    }

    if (imageFields.length < 1) {
      validationErrors.push('Add at least 1 photo');
      setErrors(validationErrors);
      isValid = false;
    }

    return isValid;
  };

  const onSubmit = () => {
    let tempObj = { publicData: {} };
    if (imageFields.length > 0) {
      tempObj.images = getImageFromState();
    }

    if (videos.length > 0) {
      tempObj.publicData.videos = videos;
    }

    if (!validateFields()) {
      notifyError('Has non valid inputs');
      return;
    }

    // ── Publish-time gate ────────────────────────────────
    // Per Subscription Gating Spec §1: clicking Publish without an
    // active subscription must NOT open a modal — it should send the
    // coach to the pricing page (the listing stays as a draft, they
    // come back and click Publish again after checkout).
    //
    // Per §5.2: publish must be blocked when required profile fields
    // are missing. Show a clear toast with the specific missing items.
    //
    // Both checks happen here BEFORE the gallery is saved so that we
    // don't write half-state on a blocked publish.
    const readiness = getPublishReadiness(currentUser);
    if (!readiness.ready) {
      if (readiness.reason === 'no_subscription') {
        notifyError(
          'Subscribe to publish your listing — your draft is saved.'
        );
        props.history.push('/account/payments/pricing');
        return;
      }
      if (readiness.reason === 'incomplete_profile') {
        const missing = readiness.missingFields.join(' and ');
        notifyError(
          `Complete your profile to publish: please add ${missing}.`
        );
        props.history.push('/profile-settings');
        return;
      }
      // 'unknown' / 'count_unknown' / fail-open: let the publish go
      // through. The post-create /set-premium sync and any future
      // sweep job will reconcile if the listing ends up over-cap.
    }

    onUpdateListing('gallery', { ...tempObj, id: listing.id }).then(res => {
      //publish if not already
      onPublishListingDraft(listing.id).then(() => {
        props.history.push(`/listings`);
        notifySuccess('Saved');
      });
    });
  };

  useEffect(() => {
    let tempArray = [];
    for (let i = 0; i < images.length; i++) {
      tempArray[i] = { id: images[i].id, src: images[i].attributes.variants['landscape-crop'].url };
    }
    setImageFields(tempArray);

    if (listing.attributes.publicData && listing.attributes.publicData.videos) {
      setVideos(listing.attributes.publicData.videos);
    }
  }, []);

  return (
    <div className={css.wrapper}>
      <div className={css.video}>
        <p>Add video URL</p>
        <p className={css.hint}>Paste a link to a YouTube or Vimeo video that introduces you or showcases your coaching style. Optional but highly recommended.</p>
        {videos.map((item, index) => {
          return (
            <div key={index}>
              <div className={css.inputContainer}>
                <input
                  className={css.input}
                  input="text"
                  value={item}
                  onChange={e => inputToState(index, e)}
                />
                <div className={css.remove} onClick={() => removeInput(index)}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </div>
              {videos.length === index + 1 && (
                <div onClick={() => addInput()} className={css.addButton}>
                  {/*<DashboardAddButton onClick={() => addInput()} />*/}
                  <FontAwesomeIcon  icon={faPlusSquare} />
                  <div>Add URL</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={css.video}>
        <p>Add photos</p>
        <p className={css.hint}>Upload at least one photo. Professional headshots or images of you coaching work best. You can crop each image after selecting it.</p>
        <div className={css.imageView}>
          {imageFields.map((item, index) => (
            <div key={index}>
              <img alt="listing" src={item.src} />
              <div className={css.removePic} onClick={() => removePic(item.id.uuid)}>
                <FontAwesomeIcon icon={faTimes} />
              </div>
            </div>
          ))}
          <div>
            <input id="ListingImagePicker" type="file" accept="image/*" onChange={fileUpload} />
            <label htmlFor="ListingImagePicker">
              {uploading ? (
                <p>Uploading</p>
              ) : (
                <FontAwesomeIcon className={css.imagePickerIcon} icon={faPlus} />
              )}
            </label>
          </div>
        </div>
      </div>
      <div className={css.errors}>
        {errors.map((item, index) => (
          <p key={index} className={css.error}>
            {item}
          </p>
        ))}
      </div>
      {/*<div className={css.next}>*/}
      {/*  <p>Save & Submit</p>*/}
      {/*  <DashboardAddButton onClick={() => onSubmit()} icon={faArrowRight} />*/}
      {/*</div>*/}
      <div onClick={() => onSubmit()} className={css.button}>
        Submit
      </div>

      <ImageCropModal
        isOpen={cropModalOpen}
        imageSrc={cropImageSrc}
        fileName={cropFileName}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
        cropShape="rect"
        aspect={4 / 3}
        title="Adjust your ad image"
        subtitle="Zoom and drag to frame your image"
        saveLabel="Use image"
      />
    </div>
  );
}

export default withRouter(Gallery);
